// КОРПУС КОПИ ПАНЕЛЕЙ A/B/C (ШАГ 3) — данными, без экрана. B5-1.
//
// ИСТОЧНИК — байт борда `Life Map - s3 Deck v8.html`: `PANES` (`:493–495`), `LAYERS` (`:382–388`),
// `GROUPS` (`:390–397`), `FIELDS` (`:399–401`), приписка панели C (`:505`), чип и плейсхолдер
// свободной строки BG3 (`:491–492`), листы `?` групп (`:641–643`). Сверка машинная —
// `src/render/__parity__/wizard-s3-copy.test.jsx`.
//
// ⛔ ЭТАЛОН СВЕРКИ — ТАБЛИЦА ОТСТАВАНИЙ `WIZARD-CANON.md`, А НЕ СЫРОЙ БАЙТ. Для корпуса это
// значит две вещи, и обе проверяются:
//   · апостроф. Долг `s711.d2` (прямой U+0027 вместо кручёного U+2019) касается Т-ЯРЛЫКА
//     `didn't allow yourself` — это `src/quotes/prompts.js`, промпт модели, а не корпус панели.
//     В самой деке вопрос панели C несёт КРУЧЁНЫЙ апостроф (`What didn’t you allow yourself
//     here?`, `:401`), и он портируется как есть. А вот `what's left`, `couldn't let down`,
//     `don't know`, `don't regret, won't repeat`, `still don't get it` несут ПРЯМОЙ U+0027 и в
//     борде, и в `contracts.js` — эти под `s711.d2` не попадают и «чинить» их нельзя: критерий
//     решения назван по конкретному ярлыку, а не по классу символа;
//   · `шаг 4` и `the layered map` в корпусе не встречаются вовсе — они из микро-строки финала
//     (`deckDone.js`, `s824.d4`). Отрицательное утверждение про них стоит на всём наборе модулей
//     шага 3 сразу, а не на одном файле.
//
// ⭐ `max` ГРУПП — НЕ ВТОРОЙ ИСТОЧНИК. Потолки выбора уже объявлены данными в `src/contracts.js`
// (`BUTTON_GROUPS`, `s510.d2`), и здесь они НЕ повторяются: группа несёт только копи, а `max` и
// список опций берутся из контракта. Причина названа в самом контракте: `max` лежал только в
// борде, и контракт не мог ответить «сколько человеку можно выбрать». Завести вторую копию здесь
// значило бы вернуть ровно ту дыру, которую `s510.d2` закрыл.
//
// ⛔ ПОДПИСЬ `layer glossary · catalog s175–s179` (борд `:640`) НА ЭКРАН НЕ ИДЁТ — §19 спеки,
// слово Кирилла. Поэтому её здесь нет вовсе, а не «есть, но не печатается»: строка, лежащая в
// корпусе, рано или поздно доезжает до экрана.
//
// ⭐ ОДНО ИСКЛЮЧЕНИЕ ИЗ «ИСТОЧНИК — БАЙТ БОРДА» ЗАВЕДЕНО 26.09: `A11Y_COPY`, две строки для
// клавиатуры и озвучки. У борда их нет и быть не может — у него клавиатуры нет вовсе. Они стоят
// отдельным объектом и помечены; всё остальное в файле по-прежнему байт борда.
//
// ⛔ ГЛОССАРИЯ СЛОЁВ (`GLOSS`, борд `:373–380`) ЗДЕСЬ ТОЖЕ НЕТ. Он не входит в скоуп B5-1 (листы
// `?` панели A — отдельный предмет), и класть его сюда авансом значило бы объявить перенесённым
// то, что не переносилось.

import { BUTTON_GROUPS, BUTTON_GROUP_KEYS, LAYERS } from '../../contracts.js';
import { GROUP_IDS } from '../toWizardInput.js';

/**
 * Три панели карточки (борд `:493–495`).
 *
 * `titleLast` — заголовок на ПОСЛЕДНЕЙ карточке: у неё всё говорится в настоящем времени
 * (правило v5). Есть он только у панели B; у A и C заголовок один на все карточки, и пустого
 * поля под него здесь нет — отсутствие ключа и значит «ветки нет».
 */
export const PANES = [
  { letter: 'A', title: 'the seven layers', sub: 'mark where it feels, not where you think' },
  {
    letter: 'B',
    title: 'how you entered and lived it',
    titleLast: 'how you are living it',
    sub: 'quick taps — four questions',
  },
  { letter: 'C', title: 'questions of the stage', sub: 'let what wants out, come out — speak it or type it' },
];

/**
 * ПАНЕЛЬ A — семь слоёв (борд `:382–388`).
 *
 * `key` — ключ контракта (`LAYERS` из `contracts.js`, порядок = ось датасетов, переставлять
 * нельзя). `name`/`question` — редакция прошедшего времени, `nameLast`/`questionLast` — настоящего,
 * для последней карточки. Имя в настоящем времени есть только у `Lasting` (`wanted to last` →
 * `want it to last`) — у остальных шести меняется только вопрос, и пустых ключей под это нет.
 *
 * `poles` — три подписи под дорожкой: левый полюс, середина, правый.
 */
export const LAYER_COPY = [
  {
    key: 'Vitality',
    name: 'vitality',
    question: 'did you have the fuel for life?',
    questionLast: 'do you have the fuel for life?',
    poles: { left: 'drained', mid: 'steady', right: 'full of life' },
  },
  {
    key: 'Security',
    name: 'sense of security',
    question: 'was the ground under you solid?',
    questionLast: 'is the ground under you solid?',
    poles: { left: 'hung by a thread', mid: 'stable', right: 'on solid ground' },
  },
  {
    key: 'Growth',
    name: 'growth',
    question: 'did it move you forward?',
    questionLast: 'is it moving you forward?',
    poles: { left: 'stuck', mid: 'shifting', right: 'growing' },
  },
  {
    key: 'Authorship',
    name: 'authorship',
    question: 'did you call the shots?',
    questionLast: 'are you calling the shots?',
    poles: { left: 'life decided', mid: 'both', right: 'I decided' },
  },
  {
    key: 'Wanting',
    name: 'wanting',
    question: 'was something pulling you?',
    questionLast: 'is something pulling you?',
    poles: { left: 'nothing pulled', mid: 'steady', right: 'pulled to many things' },
  },
  {
    key: 'Yourself',
    name: 'being yourself',
    question: 'were you living as you?',
    questionLast: 'are you living as you?',
    poles: { left: 'kept up appearances', mid: 'sometimes off', right: 'fully me' },
  },
  {
    key: 'Lasting',
    name: 'wanted to last',
    nameLast: 'want it to last',
    question: 'did you want it to last?',
    questionLast: 'do you want it to last?',
    poles: { left: 'pushed it away', mid: 'livable', right: 'wanted forever' },
  },
];

/** Метка перенесённого слоя (борд `:480`). Внимание, а не ответ — см. `carried` в `deckGate.js`. */
export const CARRY_LABEL = 'moved last card';

/**
 * ⭐ ДВЕ СТРОКИ, КОТОРЫХ В БОРДЕ НЕТ ВОВСЕ — копи web-Claude от 26.09, слоты закрыты после B5-3.
 *
 * ⚠️ ЭТО ЕДИНСТВЕННОЕ МЕСТО ФАЙЛА, ГДЕ ИСТОЧНИК НЕ БАЙТ БОРДА, и потому они стоят отдельным
 * объектом с отдельным именем, а не подмешаны к `LAYER_COPY`. Причина не косметическая: парити
 * сверяет корпус с бордом байт в байт, и строка без пары в борде, лежащая среди бордовых, читалась
 * бы как «борд её потерял». Здесь она читается как то, чем является: у борда клавиатуры нет вовсе
 * (§19 спеки деки — долг), а невидимый текст обязан быть написан кем-то, кто пишет тексты.
 *
 * Отсутствие обеих строк в борде проверяется отрицательным утверждением в парити-тесте — с обеих
 * сторон, как у известных отставаний.
 *
 * `sliderUnset` — озвучка НЕСВЕДЁННОЙ дорожки (`aria-valuetext`). ⚠️ Описывает СОСТОЯНИЕ ДОРОЖКИ,
 * а не наличие числа в конверте: значение вне ∓3 не рисуется (приёмка B5-1, вопрос 3), и слышно
 * про него то же, что видно. Расхождение со счётчиком `{n}/7`, который такое значение считает,
 * названо и принадлежит не сюда — его дом шов сабмита (блок 10).
 *
 * `layerHelp` — доступное имя кнопки `?`; на экране у неё один глиф, и без имени она звучит как «?».
 */
export const A11Y_COPY = {
  sliderUnset: 'Not set yet',
  layerHelp: 'What does this mean?',
};

/**
 * ПАНЕЛЬ B — четыре группы кнопок (борд `:390–397`).
 *
 * `id` — ключ состояния экрана (`S.grp[i][id]`), `bg` — ключ контракта. Пара живёт здесь, потому
 * что связывает два словаря, и выводить её из порядка массивов молча — ровно тот способ, каким
 * ряды ложатся не на те группы.
 *
 * `hint` есть не у всех: у группы `for` его в борде нет вовсе (`max: 1`, подсказка «сколько
 * выбрать» бессмысленна), и пустой строки под него здесь нет.
 *
 * `tip` — текст листа `?` по группе (борд `:642`).
 */
export const GROUP_COPY = [
  {
    id: 'trigger',
    bg: 'BG1',
    question: 'what triggered the transition?',
    questionLast: 'what started this stage?',
    hint: 'pick 1–3, can combine',
    tip: 'What kicked off this stage. Often it is more than one thing.',
  },
  {
    id: 'fuel',
    bg: 'BG2',
    question: 'what got you through it?',
    questionLast: 'what is getting you through it?',
    hint: 'pick 1–2, can combine',
    tip: 'Not the dream — the actual fuel that carries you.',
  },
  {
    id: 'for',
    bg: 'BG3',
    question: 'what were you living for then?',
    questionLast: 'what are you living for now?',
    tip: 'Your real reason for all of it. One answer.',
  },
  {
    id: 'left',
    bg: 'BG4',
    question: "what's left of this stage in you?",
    questionLast: 'what is this stage leaving in you?',
    hint: 'pick 1–2, can combine',
    tip: 'What stayed in you from this.',
  },
];

/** Чип и поле свободной строки BG3 (борд `:491–492`). */
export const CUSTOM_COPY = {
  chip: '+ something else',
  placeholder: 'in your words',
};

/**
 * Строка листа `?` про потолок выбора (борд `:643`).
 *
 * Собирается из `max` КОНТРАКТА, а не из своей копии числа: иначе лист и валидатор разошлись бы
 * молча, и человек прочёл бы «up to 3» там, где контракт держит два.
 */
export function pickLine(bg) {
  const max = BUTTON_GROUPS[bg] && BUTTON_GROUPS[bg].max;
  return max === 1 ? 'one answer' : `up to ${max}, they combine`;
}

/**
 * ПАНЕЛЬ C — три свободных поля (борд `:399–401`).
 *
 * `placeholderLast` есть только у `T2`: у него в борде своя редакция настоящего времени
 * (`phn`), у `T1` и `T3` плейсхолдер один на все карточки.
 *
 * ⚠️ АПОСТРОФЫ В `T3` — КРУЧЁНЫЕ (U+2019), как в байте борда. Долг `s711.d2` про прямой U+0027
 * сюда не относится: он про Т-ЯРЛЫК `didn't allow yourself` в `src/quotes/prompts.js`, а не про
 * вопрос панели. Утверждение стоит в парити-тесте с обеих сторон.
 */
export const FIELD_COPY = [
  {
    id: 'T1',
    question: 'What was this stage about — for you?',
    questionLast: 'What is this stage about — for you?',
    placeholder: 'write or speak as much as you need',
  },
  {
    id: 'T2',
    question: 'What did you already know, and keep doing the other way?',
    questionLast: 'What do you already know, and keep doing the other way?',
    placeholder: 'what you knew already, but kept doing the other way',
    placeholderLast: 'what you know already, but keep doing the other way',
  },
  {
    id: 'T3',
    question: 'What didn’t you allow yourself here?',
    questionLast: 'What aren’t you allowing yourself here?',
    placeholder: 'creativity, rest, an opinion, ambition, softness…',
  },
];

/** Приписка под тремя полями (борд `:505`). */
export const FIELDS_NOTE = 'these three are skippable · the more you put in, the more your reading has to work with';

/**
 * Редакция строки под карточку: последняя карточка говорит в настоящем времени (правило v5).
 *
 * Отдельной функцией, потому что развилка одна и та же в трёх панелях, и `last && x.qn ? … : …`,
 * повторённый девять раз, разъезжается на десятом.
 */
export const forStage = (copy, lastKey, baseKey, isLast) => (
  (isLast && copy[lastKey]) ? copy[lastKey] : copy[baseKey]
);

/** Опции группы — из контракта, здесь не дублируются (`s510.d2`). */
export const optionsOf = (bg) => (BUTTON_GROUPS[bg] ? BUTTON_GROUPS[bg].options : []);

/**
 * Связки словарей, объявленные явно, чтобы их можно было ПРОВЕРИТЬ, а не вывести из порядка:
 * ключ слоя ↔ `LAYERS` контракта, `id` группы ↔ `GROUP_IDS` адаптера ↔ `BUTTON_GROUP_KEYS`.
 */
export const LAYER_KEYS = LAYER_COPY.map((l) => l.key);
export const GROUP_BG = GROUP_COPY.map((g) => g.bg);
export const CONTRACT_LINKS = { LAYERS, GROUP_IDS, BUTTON_GROUP_KEYS };
